import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Brain, RefreshCw, Check, X, RotateCcw, FlaskConical } from "lucide-react";

const ACTION_LABEL = {
  raise_price: "Raise price",
  lower_price: "Lower price",
  pause: "Pause product",
  resume: "Resume product",
  refresh_copy: "Refresh copy",
};

const ACTION_COLOR = {
  raise_price: "text-[color:var(--hq-green)]",
  lower_price: "text-[color:var(--hq-cyan)]",
  pause: "text-[color:var(--hq-red)]",
  resume: "text-[color:var(--hq-green)]",
  refresh_copy: "text-[color:var(--hq-amber)]",
};

const chf = (n) =>
  n == null
    ? "—"
    : new Intl.NumberFormat("de-CH", { style: "currency", currency: "CHF" }).format(n);

export function LearningPanel() {
  const [pending, setPending] = useState([]);
  const [performance, setPerformance] = useState([]);
  const [experiments, setExperiments] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [p, perf, exps] = await Promise.all([
        api.get("/learning/proposals?status=pending"),
        api.get("/learning/performance"),
        api.get("/experiments/?status=running"),
      ]);
      setPending(p.data.proposals || []);
      setPerformance(perf.data.products || []);
      setExperiments(exps.data.experiments || []);
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const scanNow = async () => {
    setScanning(true);
    try {
      const { data } = await api.post("/learning/scan");
      toast.success(
        `Scanned ${data.products_scanned} products · ${data.proposals_created} new proposal(s)`,
      );
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scan failed");
    } finally {
      setScanning(false);
    }
  };

  const approve = async (id) => {
    try {
      await api.post(`/learning/proposals/${id}/approve`);
      toast.success("Applied");
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not apply");
    }
  };

  const startExperiment = async (id) => {
    try {
      const { data } = await api.post("/experiments/start", { proposal_id: id });
      toast.success(`A/B experiment ${data.experiment_id} started`);
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start experiment");
    }
  };

  const reject = async (id) => {
    try {
      await api.post(`/learning/proposals/${id}/reject`);
      setPending((prev) => prev.filter((p) => p.proposal_id !== id));
      toast.success("Proposal rejected");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reject");
    }
  };

  return (
    <div className="space-y-6" data-testid="learning-panel">
      {/* Proposals */}
      <div className="hq-card p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Brain className="w-4 h-4 text-[color:var(--hq-cyan)]" />
            <div>
              <div className="font-display text-lg">Learning proposals</div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                Weekly scan · rule-based · rollback ready · {pending.length} pending
              </div>
            </div>
          </div>
          <button
            onClick={scanNow}
            disabled={scanning}
            className="hq-btn hq-btn-secondary text-xs disabled:opacity-50"
            data-testid="learning-scan-now"
          >
            <RefreshCw className={`w-3 h-3 mr-1 ${scanning ? "animate-spin" : ""}`} />
            {scanning ? "Scanning…" : "Scan now"}
          </button>
        </div>

        {loading ? (
          <div className="text-center text-white/40 py-6 font-mono text-xs">loading…</div>
        ) : pending.length === 0 ? (
          <div className="text-center text-white/40 py-6 font-mono text-xs">
            No pending proposals. The engine will re-scan every 7 days.
          </div>
        ) : (
          <table className="hq-table">
            <thead>
              <tr>
                <th>Product</th>
                <th>Action</th>
                <th>Current → Proposed</th>
                <th>Rule / reasoning</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {pending.map((p) => (
                <tr key={p.proposal_id} data-testid={`learning-row-${p.proposal_id}`}>
                  <td className="font-mono text-xs">
                    {p.handle || p.shopify_id || p.draft_id}
                  </td>
                  <td>
                    <span
                      className={`font-mono text-[10px] uppercase tracking-widest ${
                        ACTION_COLOR[p.action] || "text-white/60"
                      }`}
                    >
                      {ACTION_LABEL[p.action] || p.action}
                    </span>
                  </td>
                  <td className="font-mono text-xs">
                    {p.action.includes("price") ? (
                      <span>
                        {chf(p.current_value)} →{" "}
                        <span className="text-white">{chf(p.proposed_value)}</span>
                      </span>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="text-xs text-white/70 max-w-md">
                    <span className="font-mono text-[10px] text-white/40">
                      {p.rule_id}
                    </span>
                    <br />
                    {p.reasoning}
                  </td>
                  <td className="whitespace-nowrap">
                    <button
                      onClick={() => approve(p.proposal_id)}
                      className="hq-btn hq-btn-primary text-xs mr-2"
                      data-testid={`learning-approve-${p.proposal_id}`}
                    >
                      <Check className="w-3 h-3 mr-1" />
                      Apply
                    </button>
                    {p.action.includes("price") && (
                      <button
                        onClick={() => startExperiment(p.proposal_id)}
                        className="hq-btn hq-btn-secondary text-xs mr-2"
                        data-testid={`learning-abtest-${p.proposal_id}`}
                        title="Split traffic 50/50 and pick a winner automatically"
                      >
                        <FlaskConical className="w-3 h-3 mr-1" />
                        A/B test
                      </button>
                    )}
                    <button
                      onClick={() => reject(p.proposal_id)}
                      className="hq-btn hq-btn-ghost text-xs"
                      data-testid={`learning-reject-${p.proposal_id}`}
                    >
                      <X className="w-3 h-3 mr-1" />
                      Reject
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Running A/B experiments */}
      {experiments.length > 0 && (
        <div className="hq-card p-6" data-testid="learning-experiments">
          <div className="flex items-center gap-2 mb-4">
            <FlaskConical className="w-4 h-4 text-[color:var(--hq-amber)]" />
            <div className="font-display text-lg">Running A/B experiments</div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">
              {experiments.length} live
            </div>
          </div>
          <table className="hq-table">
            <thead>
              <tr>
                <th>Product</th>
                <th>A control</th>
                <th>B treatment</th>
                <th>Orders (A / B)</th>
                <th>Ends</th>
              </tr>
            </thead>
            <tbody>
              {experiments.map((e) => (
                <tr key={e.experiment_id} data-testid={`exp-row-${e.experiment_id}`}>
                  <td className="font-mono text-xs">{e.handle || e.shopify_id}</td>
                  <td className="font-mono text-xs">{chf(e.arm_a?.price_chf)}</td>
                  <td className="font-mono text-xs">{chf(e.arm_b?.price_chf)}</td>
                  <td className="font-mono text-xs">
                    {e.arm_a?.orders || 0} / {e.arm_b?.orders || 0}
                  </td>
                  <td className="font-mono text-[10px] text-white/50">
                    {e.ends_at ? new Date(e.ends_at).toLocaleDateString() : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Performance snapshot */}
      <div className="hq-card p-6" data-testid="learning-performance">
        <div className="flex items-center gap-2 mb-4">
          <div className="font-display text-lg">7-day product performance</div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">
            top {Math.min(performance.length, 20)} of {performance.length}
          </div>
        </div>
        {performance.length === 0 ? (
          <div className="text-center text-white/40 py-6 font-mono text-xs">
            No orders yet — the engine needs data to learn from.
          </div>
        ) : (
          <table className="hq-table">
            <thead>
              <tr>
                <th>Product</th>
                <th>Orders 7d</th>
                <th>Revenue 7d</th>
                <th>Price</th>
                <th>Margin</th>
                <th>Refund rate</th>
                <th>Age</th>
              </tr>
            </thead>
            <tbody>
              {performance.slice(0, 20).map((r) => (
                <tr key={r.shopify_id || r.draft_id}>
                  <td className="font-mono text-xs">
                    {r.title_en || r.handle || r.shopify_id}
                  </td>
                  <td className="font-mono text-xs">{r.orders_7d}</td>
                  <td className="font-mono text-xs">{chf(r.revenue_7d_chf)}</td>
                  <td className="font-mono text-xs">{chf(r.price_chf)}</td>
                  <td
                    className={`font-mono text-xs ${
                      r.margin_chf < 12 ? "text-[color:var(--hq-red)]" : ""
                    }`}
                  >
                    {chf(r.margin_chf)}
                  </td>
                  <td
                    className={`font-mono text-xs ${
                      r.refund_rate > 0.15 ? "text-[color:var(--hq-red)]" : ""
                    }`}
                  >
                    {(r.refund_rate * 100).toFixed(1)}%
                  </td>
                  <td className="font-mono text-[10px] text-white/50">{r.age_days}d</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <p className="text-white/40 text-xs">
        <RotateCcw className="w-3 h-3 inline mr-1" />
        Every applied proposal keeps the original value — one click Rollback in Audit log
        if metrics worsen.
      </p>
    </div>
  );
}
